/**
 * Landing v3 — a row that loops forever, on the phone layout (the reviews and
 * the posts; user 2026-09-29: "infinite horizontal scrolling also on
 * mobile"). It drifts on its own, as the desktop's rows do, and a finger can
 * push it either way — it has no end in either direction. A press holds it
 * still (to read a card); a flick throws it, and it slows back into its
 * drift. A drag never opens the card it started on; a tap still does.
 *
 * Cheap: ONE track holding the items twice, its drift a Web Animation run by
 * the compositor (transform only). A drag moves that same animation by its
 * clock, so there is no second mechanism to hand over to. It runs only near
 * the screen, and under reduced motion it holds still (a finger still moves
 * it). The page's own scroll is never taken: the row claims only sideways
 * drags (`touch-action: pan-y`).
 */
import { useInView, useReducedMotion } from 'framer-motion';
import {
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

import { cn } from '@/lib/utils';

/** How far a press moves before it is a drag rather than a tap, px. */
const DRAG_SLOP = 6;
/** A flick's slowdown: its speed falls by e every this many seconds. */
const FLING_DECAY_S = 0.35;
const FLING_MAX_PX_S = 4000;

/** `px` travelled along a loop `width` long, wrapped into [0, width). */
export function wrapLoop(px: number, width: number): number {
  if (!(width > 0)) return 0;
  return ((px % width) + width) % width;
}

export function LoopRow({
  speed,
  gap,
  lead = 16,
  className,
  children,
}: {
  /** The drift, px a second (the desktop row's own). */
  speed: number;
  /** Between two items, px. */
  gap: number;
  /** Before the first item on arrival, px. */
  lead?: number;
  className?: string;
  /** One run of the items; the row shows it twice. */
  children: ReactNode;
}) {
  const reduceMotion = useReducedMotion() ?? false;
  const rowRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);
  const near = useInView(rowRef, { margin: '200px 0px' });

  const loop = useRef<Animation | null>(null);
  /** One run's width, gap after its last item included: the loop's length. */
  const width = useRef(0);
  const state = useRef({ near: false, reduceMotion, held: false, hover: false });
  const fling = useRef<number | null>(null);
  const drag = useRef<{
    id: number;
    x0: number;
    y0: number;
    from: number;
    moved: boolean;
    samples: { t: number; x: number }[];
  } | null>(null);
  const dragged = useRef(false);

  const position = () => {
    const a = loop.current;
    return a ? wrapLoop((Number(a.currentTime ?? 0) / 1000) * speed, width.current) : 0;
  };
  const seek = (px: number) => {
    const a = loop.current;
    if (a) a.currentTime = (wrapLoop(px, width.current) / speed) * 1000;
  };
  const sync = () => {
    const a = loop.current;
    if (!a) return;
    const s = state.current;
    const run = s.near && !s.reduceMotion && !s.held && !s.hover;
    if (run && a.playState !== 'running') a.play();
    else if (!run && a.playState === 'running') a.pause();
  };

  // The loop, rebuilt at the same place whenever a run's width changes (a
  // font landing, the screen turning).
  useLayoutEffect(() => {
    const track = trackRef.current;
    const copy = copyRef.current;
    if (!track || !copy || typeof track.animate !== 'function') return;
    const build = () => {
      const w = copy.offsetWidth;
      if (!w || w === width.current) return;
      const at = width.current ? position() / width.current : 0;
      loop.current?.cancel();
      width.current = w;
      const a = track.animate(
        [{ transform: 'translateX(0px)' }, { transform: `translateX(${-w}px)` }],
        { duration: (w / speed) * 1000, iterations: Infinity, easing: 'linear' },
      );
      a.currentTime = at * (w / speed) * 1000;
      loop.current = a;
      sync();
    };
    build();
    const observer = new ResizeObserver(build);
    observer.observe(copy);
    return () => {
      observer.disconnect();
      if (fling.current !== null) cancelAnimationFrame(fling.current);
      loop.current?.cancel();
      loop.current = null;
      width.current = 0;
    };
  }, [speed]);

  useEffect(() => {
    state.current.near = near;
    state.current.reduceMotion = reduceMotion;
    sync();
  }, [near, reduceMotion]);

  const stopFling = () => {
    if (fling.current !== null) cancelAnimationFrame(fling.current);
    fling.current = null;
  };
  const release = () => {
    state.current.held = false;
    sync();
  };
  const throwAt = (velocity: number) => {
    let v = Math.max(-FLING_MAX_PX_S, Math.min(FLING_MAX_PX_S, velocity));
    let p = position();
    let last = performance.now();
    const step = (now: number) => {
      const dt = Math.min(64, now - last) / 1000;
      last = now;
      p += v * dt;
      v *= Math.exp(-dt / FLING_DECAY_S);
      seek(p);
      // Down to the drift's own pace: the drift takes it from here.
      if (Math.abs(v) > speed) fling.current = requestAnimationFrame(step);
      else {
        fling.current = null;
        release();
      }
    };
    fling.current = requestAnimationFrame(step);
  };

  const onPointerDown = (e: ReactPointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    stopFling();
    state.current.held = true;
    sync();
    dragged.current = false;
    drag.current = {
      id: e.pointerId,
      x0: e.clientX,
      y0: e.clientY,
      from: position(),
      moved: false,
      samples: [{ t: e.timeStamp, x: e.clientX }],
    };
  };
  const onPointerMove = (e: ReactPointerEvent) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const dx = e.clientX - d.x0;
    if (!d.moved) {
      // Sideways first, or it is the reader scrolling the page.
      if (Math.abs(dx) < DRAG_SLOP || Math.abs(dx) < Math.abs(e.clientY - d.y0)) return;
      d.moved = true;
      dragged.current = true;
      rowRef.current?.setPointerCapture(e.pointerId);
    }
    seek(d.from - dx);
    d.samples.push({ t: e.timeStamp, x: e.clientX });
    // The last tenth of a second sets the throw.
    while (d.samples.length > 2 && e.timeStamp - d.samples[0].t > 100) d.samples.shift();
  };
  const onPointerEnd = (e: ReactPointerEvent, thrown: boolean) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    drag.current = null;
    const first = d.samples[0];
    const last = d.samples[d.samples.length - 1];
    const dt = last.t - first.t;
    if (d.moved && thrown && !state.current.reduceMotion && dt > 0) {
      throwAt((-(last.x - first.x) / dt) * 1000);
    } else release();
  };

  return (
    <div
      ref={rowRef}
      // Always left to right: the loop runs one way whatever the language (the
      // reviews and posts are quoted in their own languages anyway).
      // `ltr-content`: the app's RTL sheet sets every div's direction.
      dir="ltr"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={(e) => onPointerEnd(e, true)}
      onPointerCancel={(e) => onPointerEnd(e, false)}
      onPointerEnter={(e) => {
        // A mouse resting on it holds it, as on desktop.
        if (e.pointerType !== 'mouse') return;
        state.current.hover = true;
        sync();
      }}
      onPointerLeave={(e) => {
        if (e.pointerType !== 'mouse') return;
        state.current.hover = false;
        sync();
      }}
      onClickCapture={(e) => {
        if (!dragged.current) return;
        e.preventDefault();
        e.stopPropagation();
        dragged.current = false;
      }}
      onDragStart={(e) => e.preventDefault()}
      // `overflow-clip`: a hidden overflow is still scrollable by focus.
      className={cn(
        'ltr-content w-full touch-pan-y select-none overflow-clip',
        className,
      )}>
      <div
        ref={trackRef}
        className="flex w-max items-start py-2 will-change-transform"
        style={{ paddingLeft: lead }}>
        {[0, 1].map((copy) => (
          <div
            key={copy}
            ref={copy === 0 ? copyRef : undefined}
            aria-hidden={copy === 1 || undefined}
            inert={copy === 1}
            className="flex shrink-0 items-start"
            style={{ gap, paddingRight: gap }}>
            {children}
          </div>
        ))}
      </div>
    </div>
  );
}
